import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface AnalyticsDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MonthOverMonth {
  currentMonth: string;
  previousMonth: string;
  currentExpenses: number;
  previousExpenses: number;
  currentIncome: number;
  previousIncome: number;
  expenseChange: number;
  incomeChange: number;
  expenseChangePercent: number;
  incomeChangePercent: number;
}

interface YearOverYear {
  currentYear: number;
  previousYear: number;
  currentExpenses: number;
  previousExpenses: number;
  currentIncome: number;
  previousIncome: number;
  expenseChange: number;
  incomeChange: number;
  expenseChangePercent: number;
  incomeChangePercent: number;
}

interface CategoryAverage {
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  categoryColor: string;
  type: 'income' | 'expense';
  totalAmount: number;
  averagePerTransaction: number;
  transactionCount: number;
  averagePerMonth: number;
}

interface TopMerchant {
  merchantName: string;
  totalSpent: number;
  transactionCount: number;
  averageTransaction: number;
  category?: {
    name: string;
    icon: string;
    color: string;
  };
}

interface HeatmapCell {
  dayOfWeek: number;
  hour: number;
  amount: number;
  transactionCount: number;
}

interface CashflowForecast {
  month: string;
  predictedIncome: number;
  predictedExpenses: number;
  predictedNet: number;
  confidence: 'high' | 'medium' | 'low';
}

interface AnalyticsData {
  monthOverMonth: MonthOverMonth | null;
  yearOverYear: YearOverYear | null;
  categoryAverages: CategoryAverage[];
  topMerchants: TopMerchant[];
  spendingHeatmap: HeatmapCell[];
  cashflowForecast: CashflowForecast[];
}

export default function AnalyticsDashboardModal({ isOpen, onClose }: AnalyticsDashboardModalProps) {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadAnalytics();
    }
  }, [isOpen]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAnalytics();
      setAnalytics(data.analytics);
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} MAD`;
  const formatPercent = (percent: number) => `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;

  const getConfidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high': return 'bg-emerald-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-red-500';
    }
  };

  const getDayName = (day: number) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-7xl max-h-[90vh] overflow-y-auto bg-white border-4 border-black">
        <div className="absolute -top-1 -right-1 w-full h-full bg-purple-500 -z-10"></div>

        {/* Header */}
        <div className="sticky top-0 bg-black text-white p-6 border-b-4 border-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-4xl font-black uppercase tracking-tight mb-2">
                📊 Advanced Analytics Dashboard
              </h2>
              <p className="text-sm font-mono text-gray-300 uppercase tracking-wider">
                Deep insights into your financial patterns
              </p>
            </div>
            <button
              onClick={onClose}
              className="relative px-6 py-3 bg-red-500 text-white font-black border-4 border-white transition-transform hover:translate-x-1 hover:-translate-y-1"
            >
              <div className="absolute -top-1 -right-1 w-full h-full bg-red-700 -z-10"></div>
              ✕ CLOSE
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-black"></div>
            </div>
          ) : error ? (
            <div className="bg-red-500 border-4 border-black p-6 text-white">
              <h3 className="text-2xl font-black mb-2">ERROR</h3>
              <p className="font-mono">{error}</p>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Month-over-Month Comparison */}
              {analytics.monthOverMonth && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-blue-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    📈 Month-over-Month
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Expenses Comparison */}
                    <div className="border-4 border-black p-4 bg-red-50">
                      <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">
                        Expenses Trend
                      </div>
                      <div className="text-4xl font-black mb-2">
                        {formatCurrency(analytics.monthOverMonth.currentExpenses)}
                      </div>
                      <div className={`inline-block px-3 py-1 font-black text-lg ${
                        analytics.monthOverMonth.expenseChange < 0 ? 'bg-emerald-500' : 'bg-red-500'
                      } text-white border-2 border-black`}>
                        {formatPercent(analytics.monthOverMonth.expenseChangePercent)}
                      </div>
                      <div className="mt-2 text-sm font-mono text-gray-600">
                        vs {formatCurrency(analytics.monthOverMonth.previousExpenses)} last month
                      </div>
                    </div>

                    {/* Income Comparison */}
                    <div className="border-4 border-black p-4 bg-emerald-50">
                      <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">
                        Income Trend
                      </div>
                      <div className="text-4xl font-black mb-2">
                        {formatCurrency(analytics.monthOverMonth.currentIncome)}
                      </div>
                      <div className={`inline-block px-3 py-1 font-black text-lg ${
                        analytics.monthOverMonth.incomeChange > 0 ? 'bg-emerald-500' : 'bg-red-500'
                      } text-white border-2 border-black`}>
                        {formatPercent(analytics.monthOverMonth.incomeChangePercent)}
                      </div>
                      <div className="mt-2 text-sm font-mono text-gray-600">
                        vs {formatCurrency(analytics.monthOverMonth.previousIncome)} last month
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Year-over-Year Comparison */}
              {analytics.yearOverYear && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-yellow-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    📅 Year-over-Year
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Expenses Comparison */}
                    <div className="border-4 border-black p-4 bg-red-50">
                      <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">
                        Annual Expenses
                      </div>
                      <div className="text-4xl font-black mb-2">
                        {formatCurrency(analytics.yearOverYear.currentExpenses)}
                      </div>
                      <div className={`inline-block px-3 py-1 font-black text-lg ${
                        analytics.yearOverYear.expenseChange < 0 ? 'bg-emerald-500' : 'bg-red-500'
                      } text-white border-2 border-black`}>
                        {formatPercent(analytics.yearOverYear.expenseChangePercent)}
                      </div>
                      <div className="mt-2 text-sm font-mono text-gray-600">
                        vs {formatCurrency(analytics.yearOverYear.previousExpenses)} last year
                      </div>
                    </div>

                    {/* Income Comparison */}
                    <div className="border-4 border-black p-4 bg-emerald-50">
                      <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">
                        Annual Income
                      </div>
                      <div className="text-4xl font-black mb-2">
                        {formatCurrency(analytics.yearOverYear.currentIncome)}
                      </div>
                      <div className={`inline-block px-3 py-1 font-black text-lg ${
                        analytics.yearOverYear.incomeChange > 0 ? 'bg-emerald-500' : 'bg-red-500'
                      } text-white border-2 border-black`}>
                        {formatPercent(analytics.yearOverYear.incomeChangePercent)}
                      </div>
                      <div className="mt-2 text-sm font-mono text-gray-600">
                        vs {formatCurrency(analytics.yearOverYear.previousIncome)} last year
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Category Averages */}
              {analytics.categoryAverages.length > 0 && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-cyan-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    📊 Category Spending Analysis
                  </h3>
                  <div className="space-y-3">
                    {analytics.categoryAverages.map((cat, idx) => (
                      <div key={idx} className="border-4 border-black p-4 bg-white flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="text-4xl">{cat.categoryIcon}</div>
                          <div>
                            <div className="font-black text-lg">{cat.categoryName}</div>
                            <div className="text-xs font-mono text-gray-500">
                              {cat.transactionCount} transactions • Avg per month: {formatCurrency(cat.averagePerMonth)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-3xl font-black ${cat.type === 'expense' ? 'text-red-500' : 'text-emerald-500'}`}>
                            {formatCurrency(cat.totalAmount)}
                          </div>
                          <div className="text-sm font-mono text-gray-600">
                            {formatCurrency(cat.averagePerTransaction)}/tx
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Merchants */}
              {analytics.topMerchants.length > 0 && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-pink-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    🏪 Top Merchants
                  </h3>
                  <div className="space-y-3">
                    {analytics.topMerchants.map((merchant, idx) => (
                      <div key={idx} className="border-4 border-black p-4 bg-white flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="px-3 py-1 bg-black text-white font-black text-xl">
                            #{idx + 1}
                          </div>
                          <div>
                            <div className="font-black text-lg">{merchant.merchantName}</div>
                            <div className="text-xs font-mono text-gray-500">
                              {merchant.transactionCount} transactions
                              {merchant.category && (
                                <span className="ml-2">
                                  {merchant.category.icon} {merchant.category.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-black text-red-500">
                            {formatCurrency(merchant.totalSpent)}
                          </div>
                          <div className="text-sm font-mono text-gray-600">
                            {formatCurrency(merchant.averageTransaction)}/tx
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Spending Heatmap */}
              {analytics.spendingHeatmap.length > 0 && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-orange-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    🔥 Spending Heatmap
                  </h3>
                  <p className="text-sm font-mono text-gray-600 mb-4">
                    When do you spend the most? (Day of week × Hour of day)
                  </p>
                  <div className="overflow-x-auto">
                    <div className="inline-block min-w-full">
                      <div className="grid grid-cols-8 gap-1">
                        {/* Header row */}
                        <div className="font-black text-xs"></div>
                        {[0, 1, 2, 3, 4, 5, 6].map(day => (
                          <div key={day} className="font-black text-xs text-center p-2">
                            {getDayName(day)}
                          </div>
                        ))}

                        {/* Heatmap rows by hour */}
                        {Array.from({ length: 24 }, (_, hour) => (
                          <>
                            <div key={`label-${hour}`} className="font-mono text-xs p-2 font-bold">
                              {hour}:00
                            </div>
                            {[0, 1, 2, 3, 4, 5, 6].map(day => {
                              const cell = analytics.spendingHeatmap.find(
                                c => c.dayOfWeek === day && c.hour === hour
                              );
                              const intensity = cell ? Math.min(cell.amount / 1000, 1) : 0;
                              return (
                                <div
                                  key={`${day}-${hour}`}
                                  className="border-2 border-black p-2 text-center text-xs font-mono"
                                  style={{
                                    backgroundColor: intensity > 0
                                      ? `rgba(239, 68, 68, ${0.1 + intensity * 0.9})`
                                      : '#f3f4f6'
                                  }}
                                  title={cell ? `${formatCurrency(cell.amount)} (${cell.transactionCount} tx)` : 'No data'}
                                >
                                  {cell && cell.transactionCount > 0 ? cell.transactionCount : ''}
                                </div>
                              );
                            })}
                          </>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cashflow Forecast */}
              {analytics.cashflowForecast.length > 0 && (
                <div className="relative bg-white border-4 border-black p-6">
                  <div className="absolute -top-1 -left-1 w-full h-full bg-emerald-400 -z-10"></div>
                  <h3 className="text-3xl font-black mb-4 uppercase">
                    🔮 3-Month Cashflow Forecast
                  </h3>
                  <div className="space-y-3">
                    {analytics.cashflowForecast.map((forecast, idx) => (
                      <div key={idx} className="border-4 border-black p-4 bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="font-black text-xl">{forecast.month}</div>
                            <div className={`px-3 py-1 ${getConfidenceColor(forecast.confidence)} text-white font-black text-xs border-2 border-black`}>
                              {forecast.confidence.toUpperCase()} CONFIDENCE
                            </div>
                          </div>
                          <div className={`text-2xl font-black ${
                            forecast.predictedNet >= 0 ? 'text-emerald-500' : 'text-red-500'
                          }`}>
                            Net: {forecast.predictedNet >= 0 ? '+' : ''}{formatCurrency(forecast.predictedNet)}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="border-2 border-black p-3 bg-emerald-50">
                            <div className="text-xs font-mono text-gray-500 uppercase">Predicted Income</div>
                            <div className="text-xl font-black text-emerald-600">
                              {formatCurrency(forecast.predictedIncome)}
                            </div>
                          </div>
                          <div className="border-2 border-black p-3 bg-red-50">
                            <div className="text-xs font-mono text-gray-500 uppercase">Predicted Expenses</div>
                            <div className="text-xl font-black text-red-600">
                              {formatCurrency(forecast.predictedExpenses)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
